// src/modules/goldVault/pages/TransferApprovals.jsx
// HO/Branch-Manager view: transfers awaiting approval, routed through the
// INVENTORY_TRANSFER_APPROVAL workflow. Opening a row shows the full flow —
// which template triggered, every step, who can approve, current + next
// approver, the routing facts, and the decision history (TransferApprovalDetail).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Tag, Typography, Row, Col, Spin, Drawer,
} from 'antd';
import { ReloadOutlined, AuditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTransfers } from '../api';
import TransferApprovalDetail from './TransferApprovalDetail';

const { Title } = Typography;
const STATUS_COLOR = {
  CREATED: 'gold', APPROVED: 'green', REJECTED: 'red',
  PACKED: 'cyan', DISPATCHED: 'orange',
};
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

export default function TransferApprovals() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [drawer, setDrawer] = useState({ open: false, transfer: null });

  useEffect(() => { load(); }, [pagination.current]);

  const load = async () => {
    setLoading(true);
    try {
      // CREATED = pending approval (transfer stays CREATED until the workflow
      // reaches a terminal APPROVED/REJECTED decision).
      const res = await getTransfers({ status: 'CREATED', page: pagination.current, limit: pagination.pageSize });
      setData(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openDetail = (transfer) => setDrawer({ open: true, transfer });
  const closeDetail = () => setDrawer({ open: false, transfer: null });

  const columns = [
    {
      title: 'Transfer No.', dataIndex: 'transferNumber', key: 'tn', width: 140,
      render: (v, r) => <Button type="link" size="small" onClick={() => openDetail(r)}>{v}</Button>,
    },
    { title: 'Status', dataIndex: 'status', key: 'st', width: 100,
      render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    { title: 'Source Branch', key: 'sb', width: 130,
      render: (_, r) => r.sourceBranch?.name || '—' },
    { title: 'Cases', dataIndex: 'caseCount', key: 'cc', width: 70 },
    { title: 'Gross Wt', dataIndex: 'grossWeight', key: 'gw', width: 100, render: fmtW },
    { title: 'Net Wt', dataIndex: 'netWeight', key: 'nw', width: 100, render: fmtW },
    { title: 'Est. Value', dataIndex: 'totalPurchaseCost', key: 'ev', width: 120,
      render: (v) => `₹${Number(v || 0).toLocaleString('en-IN')}` },
    { title: 'Business Date', dataIndex: 'businessDate', key: 'bd', width: 120,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—' },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 130,
      render: (_, r) => (
        <Button size="small" type="primary" icon={<AuditOutlined />} onClick={() => openDetail(r)}>
          Review Flow
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Transfer Approvals</Title></Col>
        <Col><Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button></Col>
      </Row>
      <Spin spinning={loading}>
        <Table
          dataSource={data} rowKey="_id" columns={columns}
          scroll={{ x: 1000 }} size="small"
          pagination={{
            current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
            onChange: (p) => setPagination((prev) => ({ ...prev, current: p })),
          }}
        />
      </Spin>

      <Drawer
        open={drawer.open}
        onClose={closeDetail}
        width={Math.min(1100, typeof window !== 'undefined' ? window.innerWidth - 40 : 1100)}
        destroyOnClose
        title={drawer.transfer
          ? `Approval Flow — ${drawer.transfer.transferNumber} (${drawer.transfer.sourceBranch?.name || 'Branch'})`
          : 'Approval Flow'}
        extra={drawer.transfer && (
          <Button size="small" onClick={() => navigate(`/vault/transfers/${drawer.transfer._id}`)}>
            Open Transfer
          </Button>
        )}
      >
        {drawer.transfer && (
          <TransferApprovalDetail
            transferId={drawer.transfer._id}
            onDecided={() => { load(); }}
          />
        )}
      </Drawer>
    </div>
  );
}
